import { ReactElement, ReactNode, RefObject, useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { loadablePlan } from './plan';
import { GridHandle } from '../Grid';
import { PlanMutationContext, useCreatePlanMutations } from './mutate';
import { LoadingContainer } from '@ff-it/ui';
import { useRequestHandler } from 'modules/campaign/block/BlockProvider';
import { useBlockRealtime } from 'modules/campaign/block/BlockRealtimeProvider';
import { EntityContextType } from 'components';
import { BlockDetails } from 'modules/campaign/block/types';
export { useHasLockedRows, usePlanMeta, useSmartOptions, usePlanSettings } from './plan';
export { gridAtom } from './grid';
export type { EntityKey, EntityRef, GridStore } from './grid';
export {
  collapsedGroupsAtom,
  maxDepthAtom,
  visibleKeysAtom,
  treeStructureAtom,
  useToggleGroup,
  useToggleAll,
} from './tree';
export type { TreeNodeMeta } from './tree';
export type { PlanStore } from './plan';
export { gridRowsAtom } from './rows';
export { usePlanMutations } from './mutate';
export { rowSummaryAtom, groupSummaryAtom, useGridSummaryRows } from './summary';
export { selectionEnabledAtom, useGridSelection, useToolbarSelection } from './selection';
export { usePlanStrategy } from './strategy';
export { usePlanPresence } from './presence';

type PlanProviderProps = {
  _revision: number;
  children: ReactNode;
  gridRef: RefObject<GridHandle>;
  mutate: EntityContextType<BlockDetails>['mutate'];
};

function LoadingBoundary({ children }: { children: ReactNode }) {
  const [value] = useAtom(loadablePlan);

  return (
    <LoadingContainer loading={value.state === 'loading'} className="flex-grow-1 flex-column d-flex">
      {value.state === 'hasData' && children}
    </LoadingContainer>
  );
}

export function PlanProvider({ _revision, children, gridRef, mutate }: PlanProviderProps): ReactElement {
  const requestHandler = useRequestHandler();
  const mutations = useCreatePlanMutations(requestHandler, gridRef, mutate);
  const { registerPlanPatchHandler } = useBlockRealtime();

  const [error, setError] = useState<unknown>(undefined);

  // Do not register realtime patching until the plan snapshot exists. While loading, an exact
  // patch follows the block coordinator's unmounted-plan fallback and the HTTP response supplies
  // the current representation.
  useEffect(() => {
    let active = true;
    let unregister: (() => void) | undefined;
    setError(undefined);

    void mutations
      .loadPlan(_revision)
      .then(() => {
        if (active) unregister = registerPlanPatchHandler(mutations.applyDiff);
      })
      .catch((error) => {
        if (active) setError(error);
      });

    return () => {
      active = false;
      unregister?.();
    };
  }, [_revision, mutations.applyDiff, mutations.loadPlan, registerPlanPatchHandler]);

  if (error) {
    throw error;
  }

  return (
    <PlanMutationContext.Provider value={mutations}>
      <LoadingBoundary>{children}</LoadingBoundary>
    </PlanMutationContext.Provider>
  );
}
